import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, GetApp, Visibility, Warning, ArrowBack, Close as CloseIcon } from '@mui/icons-material';
import faqService from '../services/faqService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const FAQ = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAddForm, setShowAddForm] = useState(false);
  const [formStep, setFormStep] = useState(1); // 1 for question, 2 for answer
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [viewingFAQ, setViewingFAQ] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [faqToPublish, setFaqToPublish] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
  });

  // Helper function to format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // API state (action locks prevent double submit)
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, runSave] = useActionLock();
  const [deleting, runDeleteConfirm] = useActionLock();
  const [publishing, runPublishConfirm] = useActionLock();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exporting, setExporting] = useState(false);
  const hasFetched = useRef(false);

  // Fetch FAQs on component mount
  useEffect(() => {
    const fetchFaqs = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;

      setLoading(true);
      setError('');

      const result = await faqService.getFaqs();

      if (result.success) {
        setFaqs(result.data.results || result.data || []);
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    fetchFaqs();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const refreshFaqs = async () => {
    const result = await faqService.getFaqs();
    if (result.success) {
      setFaqs(result.data.results || result.data || []);
    }
  };

  const handleAddNew = () => {
    setFormData({
      question: '',
      answer: '',
    });
    setEditingFAQ(null);
    setFormStep(1);
    setShowAddForm(true);
  };

  const handleEdit = (faq) => {
    setFormData({
      question: faq.question,
      answer: faq.answer,
    });
    setEditingFAQ(faq);
    setFormStep(1);
    setShowAddForm(true);
  };

  const handleView = (faq) => {
    setViewingFAQ(faq);
  };

  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await faqService.deleteFaq(faqToDelete.id);

      if (result.success) {
        showSnackbar('FAQ deleted successfully');
        await refreshFaqs();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while deleting', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setFaqToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
  };

  const handlePublishClick = (faq) => {
    if (faq.status === 'published') {
      showSnackbar('This FAQ is already published', 'info');
      return;
    }

    setFaqToPublish({ ...faq, _saveAndPublish: false });
    setPublishDialogOpen(true);
  };

  const handlePublishConfirm = async () => {
    if (!faqToPublish) return;

    if (faqToPublish._saveAndPublish) {
      setPublishDialogOpen(false);
      setFaqToPublish(null);
      await handleSaveAndPublish();
      return;
    }

    try {
      const result = await faqService.publishFaq(faqToPublish.id);

      if (result.success) {
        showSnackbar('FAQ published successfully');
        await refreshFaqs();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while publishing', 'error');
    } finally {
      setPublishDialogOpen(false);
      setFaqToPublish(null);
    }
  };

  const handlePublishCancel = () => {
    setPublishDialogOpen(false);
    setFaqToPublish(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await faqService.exportToExcel();

      if (result.success) {
        // Create a blob URL and trigger download
        const blob = new Blob([result.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `faqs_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSnackbar('Excel file downloaded successfully');
      } else {
        showSnackbar(result.error || 'Failed to export Excel', 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while exporting', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleNext = () => {
    if (formStep === 1) {
      if (!formData.question.trim()) {
        showSnackbar('Please enter a question', 'error');
        return;
      }
      setFormStep(2);
    }
  };

  const handleBack = () => {
    if (formStep === 2) {
      setFormStep(1);
    } else {
      setShowAddForm(false);
      setFormStep(1);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData?.question?.trim() || !formData?.answer?.trim()) {
      showSnackbar('Please fill in both question and answer', 'error');
      return;
    }

    try {
      let result;

      if (editingFAQ) {
        result = await faqService.updateFaq(editingFAQ.id, {
          question: formData.question,
          answer: formData.answer,
        });
      } else {
        result = await faqService.createFaq({
          question: formData.question,
          answer: formData.answer,
        });
      }

      if (result.success) {
        showSnackbar(editingFAQ ? 'FAQ updated successfully' : 'FAQ saved as draft');
        await refreshFaqs();
        setShowAddForm(false);
        setFormStep(1);
        setEditingFAQ(null);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while saving', 'error');
    }
  };

  const handleSaveAndPublishClick = () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      showSnackbar('Please fill in both question and answer', 'error');
      return;
    }

    setFaqToPublish({ _saveAndPublish: true });
    setPublishDialogOpen(true);
  };

  const handleSaveAndPublish = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      showSnackbar('Please fill in both question and answer', 'error');
      return;
    }

    try {
      let result;

      if (editingFAQ) {
        // Update first, then publish
        result = await faqService.updateFaq(editingFAQ.id, {
          question: formData.question,
          answer: formData.answer,
        });

        if (result.success && result.data.status !== 'published') {
          result = await faqService.publishFaq(editingFAQ.id);
        }
      } else {
        // Create and publish in one call
        result = await faqService.createAndPublishFaq({
          question: formData.question,
          answer: formData.answer,
        });
      }

      if (result.success) {
        showSnackbar(editingFAQ ? 'FAQ updated and published' : 'FAQ created and published successfully');
        await refreshFaqs();
        setShowAddForm(false);
        setFormStep(1);
        setEditingFAQ(null);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while publishing', 'error');
    }
  };

  // View FAQ Page
  if (viewingFAQ) {
    return (
      <Box sx={{
        backgroundColor: '#F8FAFC',
        minHeight: '100vh',
        padding: { xs: 2, sm: 2.5, md: 3 }
      }}>
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 1.5, sm: 2 },
            marginBottom: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            color: 'white',
            border: '1px solid #ECF0F1',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => setViewingFAQ(null)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '20px' }}>
              View FAQ
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            padding: 3,
            borderRadius: 2,
            border: '1px solid #ECF0F1',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, marginBottom: 3 }}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Status
              </Typography>
              <Chip
                label={viewingFAQ.status === 'published' ? 'Published' : 'Draft'}
                sx={{
                  backgroundColor:
                    viewingFAQ.status === 'published' ? '#D4F4DD' : '#FFF4E6',
                  color: viewingFAQ.status === 'published' ? '#2E7D32' : '#B17C42',
                  fontWeight: 600,
                  fontSize: '13px',
                  height: '28px',
                  textTransform: 'capitalize',
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Published On
              </Typography>
              <Typography
                sx={{
                  color: '#1E293B',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                {viewingFAQ.status === 'published' && viewingFAQ.updated_at
                  ? formatDate(viewingFAQ.updated_at)
                  : 'Not Published'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ marginY: 3 }} />

          <Box sx={{ marginBottom: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 2,
              }}
            >
              FAQ - Query
            </Typography>
            <Paper
              elevation={0}
              sx={{
                padding: 2.5,
                backgroundColor: '#F8FAFC',
                border: '1px solid #ECF0F1',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  color: '#2C3E50',
                  fontSize: '14px',
                  lineHeight: 1.8,
                  '& p': { margin: 0, marginBottom: '0.5em' },
                  '& p:last-child': { marginBottom: 0 },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& u': { textDecoration: 'underline' },
                  '& s': { textDecoration: 'line-through' },
                }}
                dangerouslySetInnerHTML={{ __html: viewingFAQ.question }}
              />
            </Paper>
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 2,
              }}
            >
              FAQ - Answer
            </Typography>
            <Paper
              elevation={0}
              sx={{
                padding: 2.5,
                backgroundColor: '#F8FAFC',
                border: '1px solid #ECF0F1',
                borderRadius: 2,
                minHeight: '150px',
              }}
            >
              <Box
                sx={{
                  color: '#2C3E50',
                  fontSize: '14px',
                  lineHeight: 1.8,
                  '& p': { margin: 0, marginBottom: '0.5em' },
                  '& p:last-child': { marginBottom: 0 },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& u': { textDecoration: 'underline' },
                  '& s': { textDecoration: 'line-through' },
                }}
                dangerouslySetInnerHTML={{ __html: viewingFAQ.answer }}
              />
            </Paper>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3, gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => setViewingFAQ(null)}
              sx={{
                borderColor: '#ECF0F1',
                color: '#2C3E50',
                textTransform: 'none',
                paddingX: 2.5,
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#4E83BD',
                  backgroundColor: '#F8FAFC',
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setViewingFAQ(null);
                handleEdit(viewingFAQ);
              }}
              sx={{
                backgroundColor: '#4E83BD',
                textTransform: 'none',
                paddingX: 3,
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#3A6A99',
                },
              }}
            >
              Edit FAQ
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Add/Edit FAQ Dialog is now at the bottom of the component

  // List View
  return (
    <Box sx={{
      backgroundColor: '#F8FAFC',
      minHeight: '100vh',
      padding: { xs: 2, sm: 2.5, md: 3 }
    }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          color: 'white',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' } }}>
            Business Policy <span style={{ fontSize: '24px' }}>&#8594;</span> FAQ
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            <Button
              variant="contained"
              onClick={handleAddNew}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '11px', sm: '13px' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Add FAQ
            </Button>

            <Button
              variant="contained"
              onClick={handleExport}
              disabled={exporting}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '11px', sm: '13px' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              {exporting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: 'white' }} />
                  <span>Exporting...</span>
                </Box>
              ) : (
                'Export as Excel'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} sx={{ color: '#4E83BD' }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table - Desktop View */}
      {!loading && !error && !isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #ECF0F1',
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#4E83BD',
                  }}
                >
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    FAQ - Query
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    FAQ - Answer
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Published On
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faqs.map((faq, index) => (
                  <TableRow
                    key={faq.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: '#E8F4F8',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                        maxWidth: '250px',
                        verticalAlign: 'top',
                      }}
                    >
                      <Box
                        dangerouslySetInnerHTML={{ __html: faq.question }}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                          '& p': { margin: 0 },
                          '& *': { margin: 0 },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                        maxWidth: '300px',
                        verticalAlign: 'top',
                      }}
                    >
                      <Box
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                          '& p': { margin: 0 },
                          '& *': { margin: 0 },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      <Chip
                        label={faq.status === 'published' ? 'Published' : 'Draft'}
                        size="small"
                        sx={{
                          backgroundColor:
                            faq.status === 'published' ? '#D4F4DD' : '#FFF4E6',
                          color: faq.status === 'published' ? '#059669' : '#B17C42',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '22px',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      {faq.status === 'published' && faq.updated_at
                        ? formatDate(faq.updated_at)
                        : '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(faq)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                          title="Edit FAQ"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(faq)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                          title="Delete FAQ"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handlePublishClick(faq)}
                          disabled={publishing === faq.id || faq.status === 'published'}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                            '&.Mui-disabled': {
                              color: '#94a3b8',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                          title={faq.status === 'published' ? 'Already Published' : 'Publish FAQ'}
                        >
                          {publishing === faq.id ? (
                            <CircularProgress size={16} sx={{ color: '#2ECC71' }} />
                          ) : (
                            <GetApp fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleView(faq)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {faqs.length === 0 && (
            <Box sx={{ padding: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No FAQs Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first FAQ
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Add FAQ
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Mobile Card View */}
      {!loading && !error && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {faqs.map((faq, index) => (
            <Paper
              key={faq.id}
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #ECF0F1',
                backgroundColor: '#FFFFFF',
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  backgroundColor: '#4E83BD',
                  padding: '10px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Chip
                  label={faq.status === 'published' ? 'Published' : 'Draft'}
                  size="small"
                  sx={{
                    backgroundColor: faq.status === 'published' ? '#D4F4DD' : '#FFF4E6',
                    color: faq.status === 'published' ? '#059669' : '#B17C42',
                    fontWeight: 600,
                    fontSize: '11px',
                    height: '22px',
                    textTransform: 'capitalize',
                  }}
                />
                <Typography sx={{ color: 'white', fontSize: '12px' }}>
                  {faq.status === 'published' && faq.updated_at
                    ? formatDate(faq.updated_at)
                    : '-'}
                </Typography>
              </Box>

              {/* Card Content */}
              <Box sx={{ padding: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  Question
                </Typography>
                <Box
                  dangerouslySetInnerHTML={{ __html: faq.question }}
                  sx={{
                    color: '#2C3E50',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    marginBottom: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    '& p': { margin: 0 },
                    '& *': { margin: 0 },
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  Answer
                </Typography>
                <Box
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                  sx={{
                    color: '#2C3E50',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    '& p': { margin: 0 },
                    '& *': { margin: 0 },
                  }}
                />
              </Box>

              {/* Card Actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  padding: '8px 16px',
                  borderTop: '1px solid #ECF0F1',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleEdit(faq)}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                  }}
                  title="Edit FAQ"
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(faq)}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                  }}
                  title="Delete FAQ"
                >
                  <Delete fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handlePublishClick(faq)}
                  disabled={publishing === faq.id || faq.status === 'published'}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                    '&.Mui-disabled': {
                      color: '#94a3b8',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  title={faq.status === 'published' ? 'Already Published' : 'Publish FAQ'}
                >
                  {publishing === faq.id ? (
                    <CircularProgress size={16} sx={{ color: '#2ECC71' }} />
                  ) : (
                    <GetApp fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleView(faq)}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}

          {/* Empty State for Mobile */}
          {faqs.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                padding: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No FAQs Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first FAQ
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Add FAQ
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#FEE2E2',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Warning sx={{ color: '#DC2626', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              Delete FAQ
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#2C3E50', fontSize: '14px', lineHeight: 1.6 }}>
            Are you sure you want to delete the FAQ{' '}
            <strong>"{faqToDelete?.question}"</strong>?
            <br />
            <br />
            This action cannot be undone and the FAQ will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={deleting}
            sx={{
              borderColor: '#ECF0F1',
              color: '#2C3E50',
              textTransform: 'none',
              paddingX: 2.5,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#4E83BD',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={() => runDeleteConfirm(handleDeleteConfirm)}
            variant="contained"
            loading={deleting}
            loadingText="Deleting..."
            sx={{
              backgroundColor: '#DC2626',
              textTransform: 'none',
              paddingX: 3,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#B91C1C',
              },
            }}
          >
            Delete
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={handlePublishCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#DBEAFE',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Warning sx={{ color: '#4E83BD', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              Confirm Publish
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#2C3E50', fontSize: '14px', lineHeight: 1.6 }}>
            Once this content is published, it cannot be edited or deleted. Please review the information carefully before continuing.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handlePublishCancel}
            variant="outlined"
            disabled={publishing}
            sx={{
              borderColor: '#ECF0F1',
              color: '#2C3E50',
              textTransform: 'none',
              paddingX: 2.5,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#4E83BD',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={() => runPublishConfirm(handlePublishConfirm)}
            variant="contained"
            loading={publishing}
            loadingText="Publishing..."
            sx={{
              backgroundColor: '#4E83BD',
              textTransform: 'none',
              paddingX: 3,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#3A6A99',
              },
            }}
          >
            Yes, Publish
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Add/Edit FAQ Dialog */}
      <Dialog
        open={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setEditingFAQ(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '18px',
            }}
          >
            {editingFAQ ? 'Edit FAQ' : 'Add New FAQ'}
          </Typography>
          <IconButton
            onClick={() => {
              setShowAddForm(false);
              setEditingFAQ(null);
            }}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flex: 1,
            }}
          >
            {/* Left Column - Question */}
            <Box
              sx={{
                flex: 1,
                padding: 3,
                backgroundColor: '#FFFFFF',
                borderRight: { md: '1px solid #ECF0F1' },
                borderBottom: { xs: '1px solid #ECF0F1', md: 'none' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#4E83BD',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#1E293B',
                    fontSize: '16px',
                  }}
                >
                  FAQ Question
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Enter Question <span style={{ color: '#DC2626' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Type question here..."
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#F8FAFC',
                    height: '100%',
                    alignItems: 'flex-start',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#4E83BD' },
                    '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#1E293B',
                  },
                }}
              />
            </Box>

            {/* Right Column - Answer */}
            <Box
              sx={{
                flex: 1,
                padding: 3,
                backgroundColor: '#F8FAFC',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#10B981',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#1E293B',
                    fontSize: '16px',
                  }}
                >
                  FAQ Answer
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Enter Answer <span style={{ color: '#DC2626' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Type answer here..."
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    alignItems: 'flex-start',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#4E83BD' },
                    '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#1E293B',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Action Buttons - Full Width Bottom */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              gap: { xs: 1, sm: 1.5 },
              padding: { xs: '16px', sm: '16px 24px' },
              borderTop: '1px solid #E2E8F0',
              backgroundColor: '#FFFFFF',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setShowAddForm(false);
                setEditingFAQ(null);
              }}
              disabled={saving || publishing}
              fullWidth={isMobile}
              sx={{
                borderColor: '#E2E8F0',
                color: '#64748b',
                textTransform: 'none',
                borderRadius: '10px',
                padding: { xs: '12px 16px', sm: '10px 24px' },
                fontSize: '14px',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#CBD5E1',
                  backgroundColor: '#F8FAFC',
                },
              }}
            >
              Cancel
            </Button>
            <ActionButton
              variant="outlined"
              loading={saving}
              onClick={() => runSave(handleSaveDraft)}
              loadingText="Saving..."
              fullWidth={isMobile}
              sx={{
                borderColor: '#4E83BD',
                color: '#4E83BD',
                textTransform: 'none',
                borderRadius: '10px',
                padding: { xs: '12px 16px', sm: '10px 24px' },
                fontSize: '14px',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#3A6A99',
                  backgroundColor: 'rgba(78, 131, 189, 0.08)',
                },
              }}
            >
              Save as Draft
            </ActionButton>
            <Button
              variant="contained"
              onClick={handleSaveAndPublishClick}
              disabled={saving || publishing}
              fullWidth={isMobile}
              sx={{
                background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                textTransform: 'none',
                borderRadius: '10px',
                padding: { xs: '12px 16px', sm: '10px 24px' },
                fontSize: '14px',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(78, 131, 189, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3A6A99 0%, #2D5580 100%)',
                  boxShadow: '0 6px 16px rgba(78, 131, 189, 0.5)',
                },
              }}
            >
              {(saving || publishing) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <span>{publishing ? 'Publishing...' : 'Saving...'}</span>
                </Box>
              ) : (
                'Save & Publish'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FAQ;
